import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import UserReviewTile from './UserReviewTile'
import useUserReviews from 'hooks/Reviews/useUserReviews'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import RatingDistribution from 'components/Common/RatingDistribution'
import RatingBreakdownItem from 'components/Common/RatingBreakdownItem'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import LineThumbsUpIcon from 'LuxKit/Icons/line/LineThumbsUpIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import Group from '../../utils/Group'
import CSSBreakpoint from '../../utils/CSSBreakpoint'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import RadioInput from 'LuxKit/Radio/RadioInput'
import { ReviewCompanionshipType } from 'api/review'
import { pluralizeToString } from 'lib/string/pluralize'
import useToggle from 'hooks/useToggle'
import IconButton from 'LuxKit/Button/IconButton'
import {
  mapRatingDistribution,
  mapRatingBreakdown,
  getRecommendPercentageText,
} from 'lib/reviews/ratingBreakdownUtils'

// Sort options for reviews panel
const SORT_OPTIONS = [
  { label: 'Most recent', value: 'newest' },
  { label: 'Highest rated', value: 'best_rating' },
  { label: 'Lowest rated', value: 'worst_rating' },
] as const

type ReviewSortType = typeof SORT_OPTIONS[number]['value']

// Map rating range to filter value
const RANGE_TO_FILTER_MAP: Record<string, string> = {
  '9.0+': 'exceptional',
  '7.0-9.0': 'good',
  '5.0-7.0': 'okay',
  '3.0-5.0': 'poor',
  '1.0-3.0': 'very-poor',
}

// Rating filter options
const RATING_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Exceptional (9-10)', value: 'exceptional' },
  { label: 'Good (7-8)', value: 'good' },
  { label: 'Okay (5-6)', value: 'okay' },
  { label: 'Poor (3-4)', value: 'poor' },
  { label: 'Terrible (1-2)', value: 'very-poor' },
] as const

// Traveller filter options
const TRAVELLER_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Partner', value: 'partner' },
  { label: 'Family', value: 'family' },
  { label: 'Friends', value: 'friends' },
  { label: 'Just myself', value: 'alone' },
] as const

// Reviews panel state type and reducer
interface ReviewsPanelState {
  loadedReviews: Array<App.Review>
  currentPage: number
  searchInput: string
  activeSearch: string
  sort: ReviewSortType
  ratingFilter: string
  travellerFilter: ReviewCompanionshipType | ''
}

type ReviewsPanelAction =
  | { type: 'SET_LOADED_REVIEWS'; payload: Array<App.Review> }
  | { type: 'SET_CURRENT_PAGE'; payload: number }
  | { type: 'INCREMENT_PAGE' }
  | { type: 'SET_SEARCH_INPUT'; payload: string }
  | { type: 'SET_SORT'; payload: ReviewSortType }
  | { type: 'SET_RATING_FILTER'; payload: string }
  | { type: 'SET_TRAVELLER_FILTER'; payload: ReviewCompanionshipType | '' }
  | { type: 'SUBMIT_SEARCH' }
  | { type: 'RESET_PAGINATION' }
  | { type: 'RESET' }

const initialState: ReviewsPanelState = {
  loadedReviews: [],
  currentPage: 1,
  searchInput: '',
  activeSearch: '',
  sort: 'newest',
  ratingFilter: '',
  travellerFilter: '',
}

function reviewsPanelReducer(state: ReviewsPanelState, action: ReviewsPanelAction): ReviewsPanelState {
  switch (action.type) {
    case 'SET_LOADED_REVIEWS':
      return { ...state, loadedReviews: action.payload }
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: action.payload }
    case 'INCREMENT_PAGE':
      return { ...state, currentPage: state.currentPage + 1 }
    case 'SET_SEARCH_INPUT':
      return { ...state, searchInput: action.payload }
    case 'SET_SORT':
      return { ...state, sort: action.payload }
    case 'SET_RATING_FILTER':
      return { ...state, ratingFilter: action.payload }
    case 'SET_TRAVELLER_FILTER':
      return { ...state, travellerFilter: action.payload }
    case 'SUBMIT_SEARCH':
      return { ...state, activeSearch: state.searchInput, currentPage: 1, loadedReviews: [] }
    case 'RESET_PAGINATION':
      return { ...state, currentPage: 1, loadedReviews: [] }
    case 'RESET':
      return initialState
    default:
      return state
  }
}

const PanelGrid = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: ${rem(280)} 1fr;
    grid-template-rows: 1fr;
    column-gap: ${rem(24)};
    overflow-y: hidden;
  }
`

const Sidebar = styled.div`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    border-bottom: none;
    overflow-y: auto;
  }
`

const RatingBreakdownSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: ${rem(16)};
  column-gap: ${rem(24)};
  justify-items: center;
`

const RatingBreakdownSectionMobile = styled(Group)`
  padding: 0 ${rem(8)};
`

const ReviewsColumn = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 0;

  ${mediaQueryUp.tablet} {
    overflow-y: hidden;
  }
`

const FilterBar = styled.div`
  padding: ${rem(16)} ${rem(20)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const SearchInputWrapper = styled.div`
  position: relative;
  width: 100%;
`

const FilterChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
`

const RadioGroup = styled.div`
  padding: ${rem(16)};
`

const ReviewsList = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(20)};
  position: relative;
  flex: 1;
  min-height: 0;

  ${mediaQueryUp.tablet} {
    overflow-y: auto;
  }
`

const EmptyState = styled.div`
  padding: ${rem(40)} ${rem(20)};
  text-align: center;
`

const SearchResultInfo = styled.div`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${rem(24)} 0;
`

const PAGE_SIZE = 9

interface Props {
  isOpen: boolean
  onClose: () => void
  property?: App.OfferProperty | App.BedbankProperty
  isLPP?: boolean
  experience?: App.ExperienceOffer
  tour?: App.Tours.TourV2Offer
  initialRatingFilter?: string
}

function ReviewsPanel({
  isOpen,
  onClose,
  property,
  isLPP,
  experience,
  tour,
  initialRatingFilter = '',
}: Props) {
  // Track the trigger element - using state so we re-run effects when it mounts
  const [triggerElement, setTriggerElement] = useState<HTMLDivElement | null>(null)

  // Reviews panel state with useReducer
  const [state, dispatch] = useReducer(reviewsPanelReducer, initialState)
  const {
    loadedReviews,
    currentPage,
    searchInput,
    activeSearch,
    sort,
    ratingFilter,
    travellerFilter,
  } = state

  // Dropdown toggle states
  const [isSortOpen, , openSort, closeSort] = useToggle()
  const [isRatingOpen, , openRating, closeRating] = useToggle()
  const [isTravellerOpen, , openTraveller, closeTraveller] = useToggle()

  // Refs for dropdown anchors
  const sortRef = useRef<HTMLButtonElement>(null)
  const ratingRef = useRef<HTMLButtonElement>(null)
  const travellerRef = useRef<HTMLButtonElement>(null)

  const offset = (currentPage - 1) * PAGE_SIZE
  const [reviewsData, fetchingReviews] = useUserReviews({
    propertyProvider: isLPP ? 'bedbank' : 'luxuryescapes',
    propertyId: property?.id,
    experienceId: experience?.id,
    tourId: tour?.id,
    useExternalReviews: true,
    useHiddenForProperty: true,
    limit: PAGE_SIZE,
    offset,
    sort,
    ratings: ratingFilter ? [ratingFilter] : undefined,
    companionship: travellerFilter ? [travellerFilter] : undefined,
  })

  // Combine previously loaded reviews with current page
  const allReviewsRaw = useMemo(() => {
    return currentPage === 1
      ? (reviewsData?.reviews ?? [])
      : [...loadedReviews, ...(reviewsData?.reviews ?? [])]
  }, [currentPage, reviewsData?.reviews, loadedReviews])

  // Apply client-side search filtering (search for whole words)
  // TODO: Replace with server side search filtering once backend supports that.
  const allReviews = useMemo(() => {
    if (!activeSearch.trim()) return allReviewsRaw

    const searchTerm = activeSearch.toLowerCase().trim() + ' '
    return allReviewsRaw.filter(review => {
      const reviewText = (review.review?.toLowerCase() ?? '') + ' '
      return reviewText.includes(searchTerm) || reviewText.includes(searchTerm.trim())
    })
  }, [allReviewsRaw, activeSearch])

  const hasMoreReviews = allReviewsRaw.length < (reviewsData?.total ?? 0)

  const onLoadMore = useCallback(() => {
    if (!fetchingReviews && hasMoreReviews) {
      dispatch({ type: 'SET_LOADED_REVIEWS', payload: allReviewsRaw })
      dispatch({ type: 'INCREMENT_PAGE' })
    }
  }, [allReviewsRaw, fetchingReviews, hasMoreReviews])

  // Reset state when panel closes
  useEffect(() => {
    if (!isOpen) {
      dispatch({ type: 'RESET' })
    }
  }, [isOpen])

  // Set initial rating filter when panel opens
  useEffect(() => {
    if (isOpen && initialRatingFilter) {
      dispatch({ type: 'SET_RATING_FILTER', payload: initialRatingFilter })
    }
  }, [isOpen, initialRatingFilter])

  // Reset pagination when filters change
  useEffect(() => {
    dispatch({ type: 'RESET_PAGINATION' })
  }, [sort, ratingFilter, travellerFilter])

  // Infinite scroll using Intersection Observer
  // Using triggerElement (state) instead of a ref so effect re-runs when element mounts
  useEffect(() => {
    if (!triggerElement || !isOpen) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !fetchingReviews && hasMoreReviews) {
          onLoadMore()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(triggerElement)

    return () => {
      observer.disconnect()
    }
  }, [triggerElement, isOpen, fetchingReviews, hasMoreReviews, onLoadMore])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  // Search handlers
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_SEARCH_INPUT', payload: e.target.value })
  }, [])

  const handleSearchSubmit = useCallback(() => {
    dispatch({ type: 'SUBMIT_SEARCH' })
  }, [])

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit()
    }
  }, [handleSearchSubmit])

  // Sort handler
  const handleSortChange = useCallback((value: ReviewSortType) => {
    dispatch({ type: 'SET_SORT', payload: value })
    closeSort()
  }, [closeSort])

  // Rating filter handler
  const handleRatingChange = useCallback((value: string) => {
    dispatch({ type: 'SET_RATING_FILTER', payload: value })
    closeRating()
  }, [closeRating])

  // Rating distribution click handler
  const handleRatingDistributionClick = useCallback((ratingRange: string) => {
    const filter = RANGE_TO_FILTER_MAP[ratingRange] || ''
    dispatch({ type: 'SET_RATING_FILTER', payload: filter })
  }, [])

  // Traveller filter handler
  const handleTravellerChange = useCallback((value: ReviewCompanionshipType | '') => {
    dispatch({ type: 'SET_TRAVELLER_FILTER', payload: value })
    closeTraveller()
  }, [closeTraveller])

  // Get current labels for chips
  const currentSortLabel = SORT_OPTIONS.find(o => o.value === sort)?.label ?? 'Most recent'
  const currentRatingLabel = RATING_OPTIONS.find(o => o.value === ratingFilter)?.label
  const currentTravellerLabel = TRAVELLER_OPTIONS.find(o => o.value === travellerFilter)?.label

  const displayableRating = property?.rating ?? experience?.rating ?? tour?.rating
  const isGoogleReviews = displayableRating?.reviewsSource === 'google'
  const offerRatingDisplayable = isOfferRatingDisplayable(displayableRating)

  // Extract rating breakdown data from property, experience, or tour
  const ratingBreakdown =
    property?.rating?.reviewsRatingBreakdown ??
    experience?.rating?.reviewsRatingBreakdown ??
    tour?.rating?.reviewsRatingBreakdown

  // Map rating distribution from backend format to frontend format
  const ratingDistributions = useMemo(
    () => mapRatingDistribution(ratingBreakdown?.ratingDistribution),
    [ratingBreakdown?.ratingDistribution]
  )

  // Map rating breakdown items from backend format to frontend format
  const ratingBreakdowns = useMemo(
    () => mapRatingBreakdown(ratingBreakdown),
    [ratingBreakdown]
  )

  // Get recommend percentage text
  const entityType = property ? 'hotel' : tour ? 'tour' : 'experience'
  const recommendText = useMemo(
    () => getRecommendPercentageText(ratingBreakdown?.recommendPercentage, entityType),
    [ratingBreakdown?.recommendPercentage, entityType]
  )

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={handleClose}
      mode="drawer-starboard"
      data-reviews-panel="true"
      size="XXL"
    >
      <ModalHeader title="Reviews" onCloseButtonClick={handleClose} />
      <ModalBody>
        <PanelGrid>
          {/* Sidebar with rating summary */}
          <Sidebar>
            <VerticalSpacer gap={24}>
              {/* Number Rating */}
              {offerRatingDisplayable && (
                <NumberRating
                  variant="xxl"
                  rating={displayableRating.score}
                  total={displayableRating.reviewsTotal}
                  ratingSubtitle={isGoogleReviews ? 'Reviews from Google' : undefined}
                />
              )}
              {!isGoogleReviews && (
                <>
                  <CSSBreakpoint min="tablet">
                    <VerticalSpacer gap={24}>
                      {/* Rating Distribution */}
                      {ratingDistributions.length > 0 && (
                        <>
                          <VerticalSpacer gap={0}>
                            {ratingDistributions.map(item => (
                              <RatingDistribution
                                key={item.id}
                                label={item.label}
                                range={item.range}
                                progress={item.progress}
                                count={item.count}
                                width={225}
                                isPanel
                                onClick={() => handleRatingDistributionClick(item.range)}
                              />
                            ))}
                          </VerticalSpacer>
                          <Divider kind="primary" direction="horizontal" />
                        </>
                      )}

                      {/* Rating Breakdown */}
                      {ratingBreakdowns.length > 0 && (
                        <>
                          <RatingBreakdownSection>
                            {ratingBreakdowns.map(item => (
                              <RatingBreakdownItem
                                key={item.id}
                                score={item.score}
                                label={item.label}
                              />
                            ))}
                          </RatingBreakdownSection>
                          <Divider kind="primary" direction="horizontal" />
                        </>
                      )}
                    </VerticalSpacer>
                  </CSSBreakpoint>
                  <CSSBreakpoint max="mobile">
                    {/* Rating Distribution */}
                    {(ratingDistributions.length > 0 || ratingBreakdowns.length > 0) && (
                      <VerticalSpacer gap={16}>
                        {/* Rating Distribution */}
                        {ratingDistributions.length > 0 && (
                          <Group direction="vertical" verticalAlign="space-between" gap={4}>
                            {ratingDistributions.map(item => (
                              <RatingDistribution
                                key={item.id}
                                label={item.label}
                                range={item.range}
                                progress={item.progress}
                                count={item.count}
                                onClick={() => handleRatingDistributionClick(item.range)}
                              />
                            ))}
                          </Group>
                        )}
                        {/* Rating Breakdown */}
                        {ratingBreakdowns.length > 0 && (
                          <RatingBreakdownSectionMobile direction="horizontal" horizontalAlign="space-between">
                            {ratingBreakdowns.map(item => (
                              <RatingBreakdownItem
                                key={item.id}
                                score={item.score}
                                label={item.label}
                              />
                            ))}
                          </RatingBreakdownSectionMobile>
                        )}
                      </VerticalSpacer>
                    )}
                  </CSSBreakpoint>

                  {/* Recommendation */}
                  {recommendText && (
                    <BodyTextBlock
                      weight="bold"
                      variant="medium"
                      startIcon={<LineThumbsUpIcon colour="highlight-secondary" />}
                    >
                      {recommendText}
                    </BodyTextBlock>
                  )}
                </>
              )}
            </VerticalSpacer>
          </Sidebar>

          {/* Reviews Column with Filter Bar and List */}
          <ReviewsColumn>
            {/* Filter Bar */}
            {!isGoogleReviews && (
              <FilterBar>
                {/* Search Input */}
                <SearchInputWrapper>
                  <TextInputOnly
                    placeholder="Search reviews"
                    value={searchInput}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    noValidationSpacing
                    endIcon={
                      <IconButton
                        kind="tertiary"
                        variant="dark"
                        size="small"
                        onClick={handleSearchSubmit}
                        aria-label="Search"
                      >
                        <LineSearchIcon />
                      </IconButton>
                    }
                  />
                </SearchInputWrapper>

                {/* Filter Chips */}
                <FilterChips>
                  {/* Sort Dropdown */}
                  <AssistChip
                    ref={sortRef}
                    size="medium"
                    variant="filled"
                    onClick={openSort}
                  >
                    Sort: {currentSortLabel}
                  </AssistChip>
                  <DropdownList
                    size="S"
                    open={isSortOpen}
                    onClose={closeSort}
                    anchorRef={sortRef}
                    triggerRef={sortRef}
                  >
                    <RadioGroup>
                      <Group direction="vertical" gap={16}>
                        {SORT_OPTIONS.map(option => (
                          <RadioInput
                            key={option.value}
                            checked={sort === option.value}
                            onChange={() => handleSortChange(option.value)}
                            name="sort"
                            value={option.value}
                          >
                            {option.label}
                          </RadioInput>
                        ))}
                      </Group>
                    </RadioGroup>
                  </DropdownList>

                  {/* Ratings Filter Dropdown */}
                  <AssistChip
                    ref={ratingRef}
                    size="medium"
                    variant="filled"
                    onClick={openRating}
                  >
                    Ratings{ratingFilter && currentRatingLabel !== 'All' ? `: ${currentRatingLabel}` : ''}
                  </AssistChip>
                  <DropdownList
                    size="S"
                    open={isRatingOpen}
                    onClose={closeRating}
                    anchorRef={ratingRef}
                    triggerRef={ratingRef}
                  >
                    <RadioGroup>
                      <Group direction="vertical" gap={16}>
                        {RATING_OPTIONS.map(option => (
                          <RadioInput
                            key={option.value}
                            checked={ratingFilter === option.value}
                            onChange={() => handleRatingChange(option.value)}
                            name="rating"
                            value={option.value}
                          >
                            {option.label}
                          </RadioInput>
                        ))}
                      </Group>
                    </RadioGroup>
                  </DropdownList>

                  {/* Travellers Filter Dropdown */}
                  <AssistChip
                    ref={travellerRef}
                    size="medium"
                    variant="filled"
                    onClick={openTraveller}
                  >
                    Travellers{travellerFilter && currentTravellerLabel !== 'All' ? `: ${currentTravellerLabel}` : ''}
                  </AssistChip>
                  <DropdownList
                    size="S"
                    open={isTravellerOpen}
                    onClose={closeTraveller}
                    anchorRef={travellerRef}
                    triggerRef={travellerRef}
                  >
                    <RadioGroup>
                      <Group direction="vertical" gap={16}>
                        {TRAVELLER_OPTIONS.map(option => (
                          <RadioInput
                            key={option.value}
                            checked={travellerFilter === option.value}
                            onChange={() => handleTravellerChange(option.value as ReviewCompanionshipType | '')}
                            name="traveller"
                            value={option.value}
                          >
                            {option.label}
                          </RadioInput>
                        ))}
                      </Group>
                    </RadioGroup>
                  </DropdownList>
                </FilterChips>
              </FilterBar>
            )}
            {/* Search Result Info */}
            {activeSearch && allReviews.length > 0 && (
              <SearchResultInfo>
                <BodyText variant="medium">
                  {pluralizeToString('review', allReviews.length)} mentioned &apos;<strong>{activeSearch}</strong>&apos;
                </BodyText>
              </SearchResultInfo>
            )}

            {/* Reviews List */}
            <ReviewsList>
              <LoadingIndicator floating visible={fetchingReviews && currentPage === 1} />

              {/* Empty state for no search results */}
              {activeSearch && allReviews.length === 0 && !fetchingReviews && (
                <EmptyState>
                  <BodyText variant="medium">
                    There are no results for &apos;<strong>{activeSearch}</strong>&apos;
                  </BodyText>
                </EmptyState>
              )}

              {/* Empty state for no reviews with filters */}
              {!activeSearch && allReviews.length === 0 && !fetchingReviews && (
                <EmptyState>
                  <BodyText variant="medium">
                    No reviews found with the selected filters
                  </BodyText>
                </EmptyState>
              )}

              {allReviews.map(review => (
                <UserReviewTile
                  key={review.id}
                  userReview={{
                    ...review,
                    experienceId: experience?.id,
                    tourId: tour?.id,
                  }}
                  variant="panel"
                  showReviewFiltersAndUserDetails
                />
              ))}

              {/* Infinite scroll trigger */}
              <div ref={setTriggerElement}>
                {fetchingReviews && currentPage > 1 && (
                  <LoadingContainer>
                    <LoadingIndicator visible />
                  </LoadingContainer>
                )}
              </div>
            </ReviewsList>
          </ReviewsColumn>
        </PanelGrid>
      </ModalBody>
    </ModalBase>
  )
}

export default React.memo(ReviewsPanel)
